'use client';
// LiveEventCard Component - Displays live sports events with player/team information, scores, odds, and optional spreads/totals
import React from 'react';
import { useRouter } from 'next/navigation';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Box, Button, Card, Typography, useTheme } from '@mui/material';

import type { LiveEvent } from '@/types';
import { MarketButton } from '../../sports/SportsGameCard';
import { LiveEventCardHeader } from './subcomponents/LiveEventCardHeader';
import { LiveEventCardNormalLayout } from './subcomponents/LiveEventCardNormalLayout';

interface LiveEventCardProps {
  event: LiveEvent;
  onTeamClick: (event: LiveEvent, index: 1 | 2) => void;
  isSelected?: boolean;
  onSelect?: () => void;
  showSpreads?: boolean;
}

export function LiveEventCard({
  event,
  onTeamClick,
  isSelected = false,
  onSelect = () => {},
  showSpreads = false,
}: LiveEventCardProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const team1 = {
    abbr: event.team1?.abbr?.toUpperCase() || 'HME',
    name: event.team1?.name || 'Team 1',
    color: event.team1?.color || '#2563eb',
  };

  const team2 = {
    abbr: event.team2?.abbr?.toUpperCase() || 'AWY',
    name: event.team2?.name || 'Team 2',
    color: event.team2?.color || '#dc2626',
  };

  return (
    <Card
      onClick={onSelect}
      sx={{
        borderRadius: '16px',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        boxShadow: 'none',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { borderColor: isSelected ? 'primary.main' : 'action.focus' },
      }}
    >
      <LiveEventCardHeader event={event} />

      <Box sx={{ p: 2, pt: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Left Column: Teams and Mobile Betting */}
          <Box
            sx={{
              flex: 1,
              minWidth: { xs: '100%', md: 240 },
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <LiveEventCardNormalLayout
              event={event}
              onTeamClick={onTeamClick}
              showSpreads={showSpreads}
              isDarkMode={isDarkMode}
            />
          </Box>

          {/* Right Column: Desktop Betting Rows (Table Replacement) */}
          {showSpreads && (event.spread1 || event.totalOver) && (
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 1, flexShrink: 0 }}>
              {/* Moneyline Column (Custom for Live) */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MarketButton
                  label={team1.abbr}
                  price={event.price1}
                  active={true}
                  color={team1.color}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTeamClick(event, 1);
                  }}
                />
                <MarketButton
                  label={team2.abbr}
                  price={event.price2}
                  active={true}
                  color={team2.color}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTeamClick(event, 2);
                  }}
                />
              </Box>

              {/* Spread Column */}
              {event.spread1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <MarketButton
                    label={team1.abbr}
                    price={event.spread1.price}
                    line={event.spread1.label}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTeamClick(event, 1);
                    }}
                  />
                  <MarketButton
                    label={team2.abbr}
                    price={event.spread2?.price || '0¢'}
                    line={event.spread2?.label || ''}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTeamClick(event, 2);
                    }}
                  />
                </Box>
              )}

              {/* Total Column */}
              {event.totalOver && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <MarketButton
                    label="O"
                    price={event.totalOver.price}
                    line={event.totalOver.label}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTeamClick(event, 1);
                    }}
                  />
                  <MarketButton
                    label="U"
                    price={event.totalUnder?.price || '0¢'}
                    line={event.totalUnder?.label || ''}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTeamClick(event, 2);
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}

export default LiveEventCard;
